/**
 * TransactionActionSheet.tsx
 * Mobile bottom sheet for transaction actions (View Details, Edit, Delete)
 *
 * Used on mobile devices when a user taps a row in the MobileTransactionsSection grid.
 * Shows conditional actions based on transaction type:
 * - Manual transactions: View Details, Edit, Delete
 * - Trade-linked transactions: View Details only
 */

import React from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
} from '@enfomentis/ui';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import { TRANSACTION_TYPE_DISPLAY_MAP } from '@/constants';
import type { BalanceHistory } from '@/types/schema/BalanceHistory';

export interface TransactionActionSheetProps {
  /** Whether the action sheet is open */
  isOpen: boolean;
  /** Handler for closing the action sheet */
  onClose: () => void;
  /** The selected transaction (null if none selected) */
  transaction: BalanceHistory | null;
  /** Handler for viewing full transaction details */
  onViewDetails: (tx: BalanceHistory) => void;
  /** Handler for editing the transaction (manual only) */
  onEdit: (tx: BalanceHistory) => void;
  /** Handler for deleting the transaction (manual only) */
  onDelete: (tx: BalanceHistory) => void;
}

export const TransactionActionSheet: React.FC<TransactionActionSheetProps> = ({
  isOpen,
  onClose,
  transaction,
  onViewDetails,
  onEdit,
  onDelete,
}) => {
  if (!transaction) return null;

  const isTradeLinked = !!(
    transaction.sourceExecutionEventId || transaction.tradeExecutionId
  );
  const typeLabel = TRANSACTION_TYPE_DISPLAY_MAP[transaction.transactionType];

  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      placement="bottom"
    >
      <DrawerContent>
        {(_onClose) => (
          <>
            <DrawerHeader className="flex flex-col gap-1">
              <h2 className="text-lg font-semibold">{typeLabel}</h2>
              <p className="text-small text-foreground-500">
                {isTradeLinked ? 'Trade-linked transaction' : 'Manual transaction'}
              </p>
            </DrawerHeader>
            <DrawerBody>
              <div className="flex flex-col gap-3">
                <Button
                  size="md"
                  variant="bordered"
                  color="primary"
                  startContent={<Eye size={20} />}
                  onPress={() => onViewDetails(transaction)}
                  className="justify-start"
                >
                  View Details
                </Button>
                {!isTradeLinked && (
                  <>
                    <Button
                      size="md"
                      variant="bordered"
                      color="default"
                      startContent={<Pencil size={20} />}
                      onPress={() => onEdit(transaction)}
                      className="justify-start"
                    >
                      Edit Transaction
                    </Button>
                    <Button
                      size="md"
                      variant="flat"
                      color="danger"
                      startContent={<Trash2 size={20} />}
                      onPress={() => onDelete(transaction)}
                      className="justify-start"
                    >
                      Delete Transaction
                    </Button>
                  </>
                )}
              </div>
            </DrawerBody>
            <DrawerFooter>
              <Button
                size="md"
                variant="light"
                onPress={_onClose}
                className="w-full"
              >
                Cancel
              </Button>
            </DrawerFooter>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};
